import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import {
  Shield, FileText, Pill, AlertTriangle, Heart, User, Plus, CheckCircle2,
  X, Clock, Activity, Search, ChevronDown, Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const CONSENT_TYPES = ['care_delivery', 'data_sharing', 'photo_recording', 'medication_administration', 'family_access', 'research', 'third_party_sharing'];
const CONSENT_LABELS = { care_delivery: 'Care Delivery', data_sharing: 'Data Sharing', photo_recording: 'Photo/Recording', medication_administration: 'Medication', family_access: 'Family Access', research: 'Research', third_party_sharing: '3rd Party Sharing' };
const SEVERITY_COLORS = { mild: 'text-amber-400 bg-amber-500/10', moderate: 'text-orange-400 bg-orange-500/10', severe: 'text-red-400 bg-red-500/10', life_threatening: 'text-red-500 bg-red-500/20' };
const MAR_STATUS_COLORS = { given: 'bg-[#A8E6CF]/10 text-[#A8E6CF]', refused: 'bg-red-500/10 text-red-400', withheld: 'bg-amber-500/10 text-amber-400', not_available: 'bg-muted text-muted-foreground', self_administered: 'bg-blue-500/10 text-blue-400' };

export default function ClinicalCompliance() {
  const { user } = useAuth();
  const [tab, setTab] = useState('consent');
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [loading, setLoading] = useState(false);

  // Consent state
  const [consents, setConsents] = useState([]);
  const [consentDialog, setConsentDialog] = useState(false);
  const [consentForm, setConsentForm] = useState({ consent_type: 'care_delivery', status: 'given', given_by: 'client', given_by_name: '', notes: '', review_date: '' });

  // MAR state
  const [marMeds, setMarMeds] = useState([]);
  const [allergies, setAllergies] = useState([]);
  const [marRecords, setMarRecords] = useState([]);
  const [marDate, setMarDate] = useState(new Date().toISOString().split('T')[0]);
  const [medDialog, setMedDialog] = useState(false);
  const [medForm, setMedForm] = useState({ medication_name: '', dose: '', route: 'oral', frequency: 'OD', times: '', prescriber: '', is_prn: false, prn_indication: '', is_controlled: false, start_date: new Date().toISOString().split('T')[0] });
  const [allergyDialog, setAllergyDialog] = useState(false);
  const [allergyForm, setAllergyForm] = useState({ substance: '', reaction_type: '', severity: 'moderate', source: 'client' });
  const [adminDialog, setAdminDialog] = useState(null);
  const [adminForm, setAdminForm] = useState({ status: 'given', dose_given: '', actual_time: '', reason_not_given: '', notes: '', prn_reason: '', allergy_override_reason: '' });

  // Profile state
  const [profile, setProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);

  const isAdmin = ['agency_admin', 'ceo'].includes(user?.role);

  useEffect(() => { fetchClients(); }, []);

  const fetchClients = async () => {
    try {
      const { data } = await axios.get(`${API}/api/clinical/my-clients`, { withCredentials: true });
      setClients(Array.isArray(data) ? data : []);
    } catch { setClients([]); }
  };

  const fetchConsents = useCallback(async (cid) => {
    if (!cid) return;
    setLoading(true);
    try { const { data } = await axios.get(`${API}/api/clinical-compliance/consent/${cid}`, { withCredentials: true }); setConsents(data); }
    catch { setConsents([]); }
    setLoading(false);
  }, []);

  const fetchMAR = useCallback(async (cid) => {
    if (!cid) return;
    setLoading(true);
    try {
      const [medsRes, recsRes] = await Promise.all([
        axios.get(`${API}/api/clinical-compliance/mar/medications/${cid}`, { withCredentials: true }),
        axios.get(`${API}/api/clinical-compliance/mar/records/${cid}?date=${marDate}`, { withCredentials: true }),
      ]);
      setMarMeds(medsRes.data.medications || []);
      setAllergies(medsRes.data.allergies || []);
      setMarRecords(recsRes.data || []);
    } catch { setMarMeds([]); setAllergies([]); setMarRecords([]); }
    setLoading(false);
  }, [marDate]);

  const fetchProfile = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const { data } = await axios.get(`${API}/api/clinical-compliance/client/${cid}/enhanced-profile`, { withCredentials: true });
      setProfile(data);
      setProfileForm({
        nhs_number: data.nhs_number || '', preferred_name: data.preferred_name || '',
        pronouns: data.pronouns || '', gender: data.gender || '', ethnicity: data.ethnicity || '',
        religion: data.religion || '',
        gp_ods: data.gp_practice?.ods_code || '', gp_name: data.gp_practice?.name || '',
        preferred_language: data.communication_needs?.preferred_language || 'English',
        interpreter_required: data.communication_needs?.interpreter_required || false,
        format_preference: data.communication_needs?.format_preference || 'standard',
      });
    } catch { setProfile(null); }
  }, []);

  useEffect(() => {
    if (selectedClient) {
      if (tab === 'consent') fetchConsents(selectedClient);
      else if (tab === 'mar') fetchMAR(selectedClient);
      else if (tab === 'profile') fetchProfile(selectedClient);
    }
  }, [selectedClient, tab, fetchConsents, fetchMAR, fetchProfile]);

  const handleSelectClient = (cid) => { setSelectedClient(cid); };

  // Consent handlers
  const saveConsent = async () => {
    try {
      await axios.post(`${API}/api/clinical-compliance/consent`, { client_id: selectedClient, ...consentForm }, { withCredentials: true });
      toast.success('Consent recorded'); setConsentDialog(false); fetchConsents(selectedClient);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const withdrawConsent = async (id) => {
    if (!window.confirm('Withdraw this consent?')) return;
    try { await axios.put(`${API}/api/clinical-compliance/consent/${id}/withdraw`, { reason: 'Withdrawn by admin' }, { withCredentials: true }); toast.success('Consent withdrawn'); fetchConsents(selectedClient); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // MAR handlers
  const saveMedication = async () => {
    try {
      const payload = { client_id: selectedClient, ...medForm, times: medForm.times ? medForm.times.split(',').map(t => t.trim()) : [] };
      await axios.post(`${API}/api/clinical-compliance/mar/medication`, payload, { withCredentials: true });
      toast.success('Medication added to MAR'); setMedDialog(false); fetchMAR(selectedClient);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const saveAllergy = async () => {
    try {
      await axios.post(`${API}/api/clinical-compliance/allergies`, { client_id: selectedClient, ...allergyForm }, { withCredentials: true });
      toast.success('Allergy recorded'); setAllergyDialog(false); fetchMAR(selectedClient);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const administerMed = async () => {
    if (!adminDialog) return;
    try {
      const { data } = await axios.post(`${API}/api/clinical-compliance/mar/administer`, {
        client_id: selectedClient, medication_id: adminDialog.id, ...adminForm,
      }, { withCredentials: true });
      if (data.allergy_warning) toast.error(data.allergy_warning, { duration: 8000 });
      else toast.success('Administration recorded');
      setAdminDialog(null); fetchMAR(selectedClient);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // Profile handler
  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`${API}/api/clinical-compliance/client/${selectedClient}/enhanced-profile`, {
        nhs_number: profileForm.nhs_number, preferred_name: profileForm.preferred_name,
        pronouns: profileForm.pronouns, gender: profileForm.gender, ethnicity: profileForm.ethnicity,
        religion: profileForm.religion,
        gp_practice: { ods_code: profileForm.gp_ods, name: profileForm.gp_name },
        communication_needs: { preferred_language: profileForm.preferred_language, interpreter_required: profileForm.interpreter_required, format_preference: profileForm.format_preference },
      }, { withCredentials: true });
      toast.success('Profile updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSavingProfile(false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="clinical-compliance-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2"><Shield className="w-6 h-6 text-[#8B5CF6]" />Clinical Compliance</h1>
            <p className="text-sm text-muted-foreground mt-1">Consent, MAR, client demographics — CQC Reg 11/12, DSCR MODS</p>
          </div>
          <div className="w-full sm:w-64">
            <Select value={selectedClient} onValueChange={handleSelectClient}>
              <SelectTrigger className="bg-muted border-border text-foreground" data-testid="client-select"><SelectValue placeholder="Select client..." /></SelectTrigger>
              <SelectContent className="bg-background border-border">
                {clients.map(c => <SelectItem key={c.id || c.usic} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {!selectedClient ? (
          <div className="text-center py-16"><User className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-30" /><p className="text-muted-foreground">Select a client to view clinical compliance data</p></div>
        ) : (
          <>
            {/* Allergy Banner */}
            {allergies.length > 0 && (
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 flex items-start gap-3" data-testid="allergy-banner">
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-400">Allergies / Adverse Reactions</p>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {allergies.map(a => (
                      <span key={a.id} className={`text-[10px] px-2 py-0.5 rounded-full ${SEVERITY_COLORS[a.severity] || 'bg-muted text-muted-foreground'}`}>
                        {a.substance} ({a.severity})
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="flex gap-1 p-1 bg-muted rounded-lg">
              {[['consent', 'Consent'], ['mar', 'MAR Chart'], ['profile', 'Demographics']].map(([key, label]) => (
                <button key={key} onClick={() => setTab(key)} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${tab === key ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-testid={`tab-${key}`}>{label}</button>
              ))}
            </div>

            {loading ? <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div> : null}

            {/* ====== CONSENT TAB ====== */}
            {!loading && tab === 'consent' && (
              <div className="space-y-4" data-testid="consent-section">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">Consent records per CQC Reg 11 and GDPR Art 6/9</p>
                  {user?.role !== 'client' && <Button size="sm" className="btn-primary" onClick={() => setConsentDialog(true)} data-testid="add-consent-btn"><Plus className="w-4 h-4 mr-1" />Record Consent</Button>}
                </div>
                {consents.length === 0 ? (
                  <div className="text-center py-8"><Heart className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" /><p className="text-muted-foreground">No consent records. Record consent before delivering care.</p></div>
                ) : (
                  <div className="space-y-2">
                    {consents.map(c => (
                      <Card key={c.id} className={`glass-card border-border ${c.status === 'withdrawn' ? 'opacity-60' : ''}`} data-testid={`consent-${c.id}`}>
                        <CardContent className="p-4 flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${c.status === 'given' ? 'bg-[#A8E6CF]/10' : c.status === 'refused' ? 'bg-red-500/10' : 'bg-amber-500/10'}`}>
                            {c.status === 'given' ? <CheckCircle2 className="w-5 h-5 text-[#A8E6CF]" /> : c.status === 'refused' ? <X className="w-5 h-5 text-red-400" /> : <Clock className="w-5 h-5 text-amber-400" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-foreground font-medium">{CONSENT_LABELS[c.consent_type] || c.consent_type}</p>
                            <p className="text-[10px] text-muted-foreground">
                              {c.status.toUpperCase()} &bull; Given by: {c.given_by_name || c.given_by} &bull; Date: {c.date_given}
                              {c.review_date && ` &bull; Review: ${c.review_date}`}
                              {c.capacity_assessed && ' &bull; Capacity assessed'}
                            </p>
                            {c.notes && <p className="text-xs text-muted-foreground mt-0.5">{c.notes}</p>}
                          </div>
                          {c.status === 'given' && user?.role !== 'client' && (
                            <Button size="sm" variant="outline" className="text-xs border-red-500/30 text-red-400" onClick={() => withdrawConsent(c.id)} data-testid={`withdraw-${c.id}`}>Withdraw</Button>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ====== MAR TAB ====== */}
            {!loading && tab === 'mar' && (
              <div className="space-y-4" data-testid="mar-section">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Input type="date" value={marDate} onChange={e => { setMarDate(e.target.value); }} className="bg-muted border-border text-foreground w-40" data-testid="mar-date" />
                    <Button size="sm" variant="outline" className="border-border text-muted-foreground" onClick={() => fetchMAR(selectedClient)}><Activity className="w-4 h-4 mr-1" />Refresh</Button>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 text-xs" onClick={() => setAllergyDialog(true)} data-testid="add-allergy-btn"><AlertTriangle className="w-3 h-3 mr-1" />Add Allergy</Button>
                    {isAdmin && <Button size="sm" className="btn-primary text-xs" onClick={() => setMedDialog(true)} data-testid="add-med-btn"><Plus className="w-3 h-3 mr-1" />Add Medication</Button>}
                  </div>
                </div>

                {/* MAR Chart */}
                {marMeds.length === 0 ? (
                  <div className="text-center py-8"><Pill className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" /><p className="text-muted-foreground">No medications on MAR chart</p></div>
                ) : (
                  <div className="space-y-3">
                    {marMeds.map(med => {
                      const todayRecords = marRecords.filter(r => r.medication_id === med.id);
                      return (
                        <Card key={med.id} className={`glass-card border-border ${med.is_prn ? 'border-[#8B5CF6]/30' : ''}`} data-testid={`mar-med-${med.id}`}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <Pill className="w-4 h-4 text-[#00A6A0]" />
                                  <p className="text-sm text-foreground font-medium">{med.medication_name}</p>
                                  {med.is_prn && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#8B5CF6]/10 text-[#8B5CF6]">PRN</span>}
                                  {med.is_controlled && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-400">CD</span>}
                                </div>
                                <p className="text-[10px] text-muted-foreground mt-0.5">
                                  {med.dose} &bull; {med.route} &bull; {med.frequency}
                                  {med.times?.length > 0 && ` &bull; Times: ${med.times.join(', ')}`}
                                  {med.prescriber && ` &bull; Prescriber: ${med.prescriber}`}
                                </p>
                                {med.special_instructions && <p className="text-xs text-amber-400 mt-1">{med.special_instructions}</p>}
                              </div>
                              <Button size="sm" className="btn-primary text-xs" onClick={() => { setAdminDialog(med); setAdminForm({ status: 'given', dose_given: med.dose, actual_time: new Date().toTimeString().slice(0,5), reason_not_given: '', notes: '', prn_reason: '', allergy_override_reason: '' }); }} data-testid={`admin-med-${med.id}`}>
                                Record
                              </Button>
                            </div>
                            {/* Today's records */}
                            {todayRecords.length > 0 && (
                              <div className="mt-3 pt-2 border-t border-border/40 flex flex-wrap gap-1.5">
                                {todayRecords.map(r => (
                                  <span key={r.id} className={`text-[10px] px-2 py-1 rounded-md ${MAR_STATUS_COLORS[r.status] || 'bg-muted text-muted-foreground'}`}>
                                    {r.actual_time} — {r.status} {r.administered_by_name && `by ${r.administered_by_name}`}
                                  </span>
                                ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ====== DEMOGRAPHICS TAB ====== */}
            {!loading && tab === 'profile' && isAdmin && (
              <div className="space-y-4" data-testid="profile-section">
                <p className="text-sm text-muted-foreground">DSCR MODS-compliant demographics and Accessible Information Standard fields</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className="text-xs text-muted-foreground">NHS Number</label><Input value={profileForm.nhs_number || ''} onChange={e => setProfileForm(f => ({ ...f, nhs_number: e.target.value }))} placeholder="10-digit NHS Number" className="bg-muted border-border text-foreground mt-1" data-testid="nhs-number-input" /></div>
                  <div><label className="text-xs text-muted-foreground">Preferred Name</label><Input value={profileForm.preferred_name || ''} onChange={e => setProfileForm(f => ({ ...f, preferred_name: e.target.value }))} className="bg-muted border-border text-foreground mt-1" data-testid="preferred-name-input" /></div>
                  <div><label className="text-xs text-muted-foreground">Pronouns</label><Input value={profileForm.pronouns || ''} onChange={e => setProfileForm(f => ({ ...f, pronouns: e.target.value }))} placeholder="e.g. she/her, he/him, they/them" className="bg-muted border-border text-foreground mt-1" /></div>
                  <div><label className="text-xs text-muted-foreground">Gender (NHS DD)</label>
                    <select value={profileForm.gender || ''} onChange={e => setProfileForm(f => ({ ...f, gender: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm mt-1" data-testid="gender-select">
                      <option value="">Select...</option><option value="male">Male</option><option value="female">Female</option><option value="non_binary">Non-binary</option><option value="not_known">Not known</option><option value="not_specified">Not specified</option>
                    </select>
                  </div>
                  <div><label className="text-xs text-muted-foreground">Ethnicity</label><Input value={profileForm.ethnicity || ''} onChange={e => setProfileForm(f => ({ ...f, ethnicity: e.target.value }))} className="bg-muted border-border text-foreground mt-1" /></div>
                  <div><label className="text-xs text-muted-foreground">Religion</label><Input value={profileForm.religion || ''} onChange={e => setProfileForm(f => ({ ...f, religion: e.target.value }))} className="bg-muted border-border text-foreground mt-1" /></div>
                </div>
                <h3 className="text-sm font-medium text-foreground pt-2">GP Practice</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className="text-xs text-muted-foreground">ODS Code</label><Input value={profileForm.gp_ods || ''} onChange={e => setProfileForm(f => ({ ...f, gp_ods: e.target.value }))} placeholder="e.g. A12345" className="bg-muted border-border text-foreground mt-1" data-testid="gp-ods-input" /></div>
                  <div><label className="text-xs text-muted-foreground">Practice Name</label><Input value={profileForm.gp_name || ''} onChange={e => setProfileForm(f => ({ ...f, gp_name: e.target.value }))} className="bg-muted border-border text-foreground mt-1" /></div>
                </div>
                <h3 className="text-sm font-medium text-foreground pt-2">Communication Needs (AIS)</h3>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div><label className="text-xs text-muted-foreground">Preferred Language</label><Input value={profileForm.preferred_language || ''} onChange={e => setProfileForm(f => ({ ...f, preferred_language: e.target.value }))} className="bg-muted border-border text-foreground mt-1" /></div>
                  <div><label className="text-xs text-muted-foreground">Format Preference</label>
                    <select value={profileForm.format_preference || 'standard'} onChange={e => setProfileForm(f => ({ ...f, format_preference: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm mt-1">
                      <option value="standard">Standard</option><option value="large_print">Large Print</option><option value="easy_read">Easy Read</option><option value="braille">Braille</option><option value="audio">Audio</option>
                    </select>
                  </div>
                  <div className="flex items-end gap-2 pb-1">
                    <label className="relative inline-flex items-center cursor-pointer" data-testid="interpreter-toggle">
                      <input type="checkbox" checked={profileForm.interpreter_required || false} onChange={e => setProfileForm(f => ({ ...f, interpreter_required: e.target.checked }))} className="sr-only peer" />
                      <div className="w-9 h-5 bg-muted peer-checked:bg-[#00A6A0] rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-4" />
                    </label>
                    <span className="text-xs text-muted-foreground">Interpreter Required</span>
                  </div>
                </div>
                <Button className="btn-primary mt-2" onClick={saveProfile} disabled={savingProfile} data-testid="save-profile-btn">{savingProfile ? 'Saving...' : 'Save Enhanced Profile'}</Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Consent Dialog */}
      <Dialog open={consentDialog} onOpenChange={setConsentDialog}>
        <DialogContent className="bg-background border-border max-w-sm">
          <DialogHeader><DialogTitle className="text-foreground">Record Consent</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs text-muted-foreground">Consent Type</label>
              <select value={consentForm.consent_type} onChange={e => setConsentForm(f => ({ ...f, consent_type: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm mt-1" data-testid="consent-type-select">
                {CONSENT_TYPES.map(t => <option key={t} value={t}>{CONSENT_LABELS[t]}</option>)}
              </select>
            </div>
            <div><label className="text-xs text-muted-foreground">Status</label>
              <select value={consentForm.status} onChange={e => setConsentForm(f => ({ ...f, status: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm mt-1">
                <option value="given">Given</option><option value="refused">Refused</option><option value="not_assessed">Not Assessed</option>
              </select>
            </div>
            <div><label className="text-xs text-muted-foreground">Given By</label><Input value={consentForm.given_by_name} onChange={e => setConsentForm(f => ({ ...f, given_by_name: e.target.value }))} placeholder="Client name or LPA holder" className="bg-muted border-border text-foreground mt-1" /></div>
            <div><label className="text-xs text-muted-foreground">Review Date</label><Input type="date" value={consentForm.review_date} onChange={e => setConsentForm(f => ({ ...f, review_date: e.target.value }))} className="bg-muted border-border text-foreground mt-1" /></div>
            <div><label className="text-xs text-muted-foreground">Notes</label><Input value={consentForm.notes} onChange={e => setConsentForm(f => ({ ...f, notes: e.target.value }))} className="bg-muted border-border text-foreground mt-1" /></div>
            <Button className="w-full btn-primary" onClick={saveConsent} data-testid="save-consent-btn">Save Consent</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Medication Dialog */}
      <Dialog open={medDialog} onOpenChange={setMedDialog}>
        <DialogContent className="bg-background border-border max-w-md">
          <DialogHeader><DialogTitle className="text-foreground">Add Medication to MAR</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={medForm.medication_name} onChange={e => setMedForm(f => ({ ...f, medication_name: e.target.value }))} placeholder="Medication name" className="bg-muted border-border text-foreground" data-testid="med-name-input" />
            <div className="grid grid-cols-2 gap-2">
              <Input value={medForm.dose} onChange={e => setMedForm(f => ({ ...f, dose: e.target.value }))} placeholder="Dose (e.g. 500mg)" className="bg-muted border-border text-foreground" />
              <select value={medForm.route} onChange={e => setMedForm(f => ({ ...f, route: e.target.value }))} className="bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm">
                <option value="oral">Oral</option><option value="topical">Topical</option><option value="inhaled">Inhaled</option><option value="injection">Injection</option><option value="sublingual">Sublingual</option><option value="rectal">Rectal</option><option value="transdermal">Transdermal</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <select value={medForm.frequency} onChange={e => setMedForm(f => ({ ...f, frequency: e.target.value }))} className="bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm">
                <option value="OD">Once Daily (OD)</option><option value="BD">Twice Daily (BD)</option><option value="TDS">Three Times (TDS)</option><option value="QDS">Four Times (QDS)</option><option value="PRN">As Required (PRN)</option>
              </select>
              <Input value={medForm.times} onChange={e => setMedForm(f => ({ ...f, times: e.target.value }))} placeholder="08:00, 20:00" className="bg-muted border-border text-foreground" />
            </div>
            <Input value={medForm.prescriber} onChange={e => setMedForm(f => ({ ...f, prescriber: e.target.value }))} placeholder="Prescriber name" className="bg-muted border-border text-foreground" />
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-xs text-muted-foreground"><input type="checkbox" checked={medForm.is_prn} onChange={e => setMedForm(f => ({ ...f, is_prn: e.target.checked }))} />PRN</label>
              <label className="flex items-center gap-2 text-xs text-muted-foreground"><input type="checkbox" checked={medForm.is_controlled} onChange={e => setMedForm(f => ({ ...f, is_controlled: e.target.checked }))} />Controlled Drug</label>
            </div>
            {medForm.is_prn && <Input value={medForm.prn_indication} onChange={e => setMedForm(f => ({ ...f, prn_indication: e.target.value }))} placeholder="PRN indication (e.g. for pain)" className="bg-muted border-border text-foreground" />}
            <Button className="w-full btn-primary" onClick={saveMedication} data-testid="save-med-btn">Add to MAR Chart</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Allergy Dialog */}
      <Dialog open={allergyDialog} onOpenChange={setAllergyDialog}>
        <DialogContent className="bg-background border-border max-w-sm">
          <DialogHeader><DialogTitle className="text-foreground">Record Allergy</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={allergyForm.substance} onChange={e => setAllergyForm(f => ({ ...f, substance: e.target.value }))} placeholder="Substance (e.g. Penicillin)" className="bg-muted border-border text-foreground" data-testid="allergy-substance-input" />
            <Input value={allergyForm.reaction_type} onChange={e => setAllergyForm(f => ({ ...f, reaction_type: e.target.value }))} placeholder="Reaction (e.g. Anaphylaxis, Rash)" className="bg-muted border-border text-foreground" />
            <select value={allergyForm.severity} onChange={e => setAllergyForm(f => ({ ...f, severity: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm">
              <option value="mild">Mild</option><option value="moderate">Moderate</option><option value="severe">Severe</option><option value="life_threatening">Life-threatening</option>
            </select>
            <Button className="w-full btn-primary" onClick={saveAllergy} data-testid="save-allergy-btn">Record Allergy</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Administer Medication Dialog */}
      <Dialog open={!!adminDialog} onOpenChange={(o) => !o && setAdminDialog(null)}>
        <DialogContent className="bg-background border-border max-w-sm">
          <DialogHeader><DialogTitle className="text-foreground">Record Administration — {adminDialog?.medication_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <select value={adminForm.status} onChange={e => setAdminForm(f => ({ ...f, status: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm" data-testid="admin-status-select">
              <option value="given">Given</option><option value="refused">Refused</option><option value="withheld">Withheld</option><option value="not_available">Not Available</option><option value="self_administered">Self-Administered</option>
            </select>
            <div className="grid grid-cols-2 gap-2">
              <Input value={adminForm.dose_given} onChange={e => setAdminForm(f => ({ ...f, dose_given: e.target.value }))} placeholder="Dose given" className="bg-muted border-border text-foreground" />
              <Input type="time" value={adminForm.actual_time} onChange={e => setAdminForm(f => ({ ...f, actual_time: e.target.value }))} className="bg-muted border-border text-foreground" />
            </div>
            {adminForm.status !== 'given' && <Input value={adminForm.reason_not_given} onChange={e => setAdminForm(f => ({ ...f, reason_not_given: e.target.value }))} placeholder="Reason not given" className="bg-muted border-border text-foreground" />}
            {adminDialog?.is_prn && <Input value={adminForm.prn_reason} onChange={e => setAdminForm(f => ({ ...f, prn_reason: e.target.value }))} placeholder="PRN reason" className="bg-muted border-border text-foreground" />}
            <Input value={adminForm.notes} onChange={e => setAdminForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes" className="bg-muted border-border text-foreground" />
            <Button className="w-full btn-primary" onClick={administerMed} data-testid="confirm-admin-btn">Confirm Administration</Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
